import {view, Icon, Divider, Menu, MenuItem, Button, Badge} from '@sail/ui';
import {assignProps} from '@sail/engine';
import {more, check, cancel, info} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';

type ChargeCardProps = {
  chargeId: string;
  amount: number | undefined | null;
  currency: string | undefined | null;
  status: string | undefined | null;
  customerName: string | undefined | null;
  customerEmail: string | undefined | null;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.charge_card.title',
    description: 'Charge Card title',
    defaultMessage: 'Payment',
  },
  succeeded: {
    id: 'conversations_ui.charge_card.succeeded',
    description: 'Succeeded status',
    defaultMessage: 'Succeeded',
  },
  pending: {
    id: 'conversations_ui.charge_card.pending',
    description: 'Pending status',
    defaultMessage: 'Pending',
  },
  failed: {
    id: 'conversations_ui.charge_card.failed',
    description: 'Failed status',
    defaultMessage: 'Failed',
  },
});

export const ChargeCard = ({
  chargeId,
  amount,
  currency,
  status,
  customerName,
  customerEmail,
}: ChargeCardProps) => {
  const intl = useIntl();

  const displayName = () => {
    if (customerName) {
      return customerName;
    }
    if (customerEmail) {
      return customerEmail;
    }
  };

  const renderStatusBadge = () => {
    let icon;
    let type: 'positive' | 'neutral' | 'negative';
    let statusText;

    if (status === 'succeeded') {
      icon = check;
      type = 'positive';
      statusText = intl.formatMessage(messages.succeeded);
    } else if (status === 'pending') {
      icon = info;
      type = 'neutral';
      statusText = intl.formatMessage(messages.pending);
    } else if (status === 'failed') {
      icon = cancel;
      type = 'negative';
      statusText = intl.formatMessage(messages.failed);
    } else {
      return null;
    }

    if (icon && type && statusText) {
      return (
        <Badge type={type}>
          {statusText} <Icon icon={icon} size="small" />
        </Badge>
      );
    }
  };

  return (
    <view.div
      css={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        padding: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '500px',
        position: 'relative',
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'xxsmall',
        }}
      >
        <view.div css={{stack: 'x', gap: 'small'}}>
          <view.span
            css={{
              color: 'subdued',
              font: 'label.medium.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {displayName() ? (
            <view.div css={{height: 10, stack: 'x', width: 0}}>
              <Divider />
            </view.div>
          ) : null}
          {displayName() ? (
            <view.span
              css={{
                color: 'subdued',
                font: 'label.medium',
              }}
            >
              {displayName()}
            </view.span>
          ) : null}
        </view.div>
        <Menu
          trigger={
            <Button
              css={{
                boxShadow: 'none',
                position: 'absolute',
                top: 8,
                right: 8,
              }}
            >
              <Icon icon={more} size="small" css={{color: 'subdued'}} />
            </Button>
          }
          subviews={{popover: {uses: [assignProps({placement: 'top'})]}}}
        >
          <MenuItem href={`https://dashboard.stripe.com/charges/${chargeId}`}>
            Edit
          </MenuItem>
        </Menu>
        <view.div css={{stack: 'x', gap: 'small', alignItems: 'center'}}>
          {amount && currency ? (
            <view.span
              css={{
                font: 'heading.large',
              }}
            >
              {intl.formatNumber(amount / 100, {
                style: 'currency',
                currency,
              })}
            </view.span>
          ) : null}
          {renderStatusBadge()}
        </view.div>
      </view.div>
    </view.div>
  );
};
